import { DefinitionName, PropertyName } from '@sap/ux-specification-types';
import type { Definition } from 'typescript-json-schema';
import { getDefinitionKey, getDefinitionsRef, getFacetDefinitionKey, parseSchemaDefinition } from '../../common';
import type { SchemaAdjustmentParameters } from './list-report/listReport';
import { addCustomActionDefinition, addEnumForActionAnchor } from '../utils/utils';

const actionToMenuDefinitionMap = new Map<DefinitionName, DefinitionName>([
    [DefinitionName.CustomHeaderActionOP, DefinitionName.ObjectPageHeaderCustomActionMenu],
    [DefinitionName.CustomFormActionOP, DefinitionName.ObjectPageCustomActionMenu],
    [DefinitionName.CustomTableActionOP, DefinitionName.ObjectPageCustomActionMenu],
    [DefinitionName.CustomTableAction, DefinitionName.CustomActionMenu],
    [DefinitionName.ViewCustomAction, DefinitionName.ViewCustomActionMenu]
]);

/**
 * Builds the JSON Schema definition to be used as `additionalProperties` for custom actions.
 *
 * Depending on the action type (`definitionKey`), this function uses the action-to-menu mapping to decide whether
 * only an action reference is allowed, or both an action reference and a menu reference (via `anyOf`).
 *
 * @param definitionKey - Base definition name of the custom action type.
 * @param scopedActionDefinitionKey - Optional scoped definition key to use for the action `$ref`.
 * @param scopedMenuDefinitionKey - Optional scoped definition key to use for the menu `$ref` (if the mapping provides one).
 * @returns A schema fragment (`$ref` or `anyOf` with `$ref`s) that can be assigned to `additionalProperties`.
 */
export function buildAdditionalPropertiesSchemaForCustomActionsAndMenus(
    definitionKey: DefinitionName,
    scopedActionDefinitionKey?: string,
    scopedMenuDefinitionKey?: string
): Definition {
    const actionRefSchema: Definition = {
        $ref: getDefinitionsRef(scopedActionDefinitionKey ?? definitionKey)
    };
    // actionToMenuDefinitionMap contains the mapping between the action and the corresponding menu definition
    const menuDefinitionKey = actionToMenuDefinitionMap.get(definitionKey);

    if (!menuDefinitionKey) {
        return actionRefSchema;
    }

    return {
        anyOf: [actionRefSchema, { $ref: getDefinitionsRef(scopedMenuDefinitionKey ?? menuDefinitionKey) }]
    };
}

/**
 * Configures additional properties for custom actions and menus in the provided application schema.
 * This includes updating actions' positions, creating specific action menu definitions, and setting up additional properties.
 *
 * @param appSchema The application schema that needs to be updated with custom actions and menus.
 * @param actionsSchemaKey The key in the schema that references the actions definitions.
 * @param actionsPositionDefinitionKey The key for the definition related to an action's position in the schema.
 * @param lineItemId The identifier for the specific line item being processed.
 * @param customActionDefinitionName The name of the definition for the custom action being added.
 * @param scopedCustomActionDefinitionKey The key for the scoped definition specific to the custom action.
 */
export function configureAdditionalPropertiesForCustomActionsAndMenus(
    appSchema: Definition,
    actionsSchemaKey: string,
    actionsPositionDefinitionKey: string,
    lineItemId: string,
    customActionDefinitionName: DefinitionName,
    scopedCustomActionDefinitionKey: string
) {
    const customActionMenuDefinitionKey = actionToMenuDefinitionMap.get(customActionDefinitionName);
    const actionsSchema = appSchema.definitions[actionsSchemaKey] as Definition;

    if (!actionsSchema || !customActionMenuDefinitionKey) {
        return;
    }

    // Update actions position for each action - we need this for the action menu
    updateActionsPosition(appSchema, actionsSchema, actionsPositionDefinitionKey);
    // Create the specific action menu definition in the schema based on the generic action menu definition
    createCustomMenuDefinition(appSchema, lineItemId, actionsPositionDefinitionKey, customActionMenuDefinitionKey);
    const scopedCustomMenuDefinitionKey = getFacetDefinitionKey(customActionMenuDefinitionKey, lineItemId);
    actionsSchema.additionalProperties = buildAdditionalPropertiesSchemaForCustomActionsAndMenus(
        customActionDefinitionName,
        scopedCustomActionDefinitionKey,
        scopedCustomMenuDefinitionKey
    );
}

/**
 * Configures the custom action definition within the provided schema by adjusting relevant parameters and adding necessary definitions and enumerations.
 *
 * @param schemaAdjustmentParameters An object containing parameters required to adjust the schema for the custom action definition.
 * Includes the application schema, page information, generation parameters, section definition properties, and custom action details.
 */
export function configureCustomActionDefinition(schemaAdjustmentParameters: SchemaAdjustmentParameters): void {
    const {
        appSchema,
        v4Page,
        sectionActionsDefinitionKey,
        scopedCustomActionDefinitionKey,
        sectionIdInManifest,
        customActionPosition,
        facetDefinitionKey,
        customActionDefinitionName,
        logger
    } = schemaAdjustmentParameters;

    addCustomActionDefinition(
        appSchema,
        v4Page,
        logger,
        scopedCustomActionDefinitionKey,
        sectionActionsDefinitionKey,
        sectionIdInManifest
    );
    addEnumForActionAnchor(
        appSchema,
        sectionActionsDefinitionKey,
        customActionPosition ?? DefinitionName.CustomActionPosition
    );

    configureAdditionalPropertiesForCustomActionsAndMenus(
        appSchema,
        sectionActionsDefinitionKey,
        customActionPosition ?? DefinitionName.CustomActionPosition,
        facetDefinitionKey,
        customActionDefinitionName,
        scopedCustomActionDefinitionKey
    );
}

/**
 * Updates the position references of actions within a schema by linking them to a specified position identifier.
 *
 * @param {Definition} appSchema - The main application schema containing all definitions.
 * @param {Definition} schemaForActions - A schema that defines the actions to be updated.
 * @param {string} positionId - The identifier of the position to which the actions should be linked.
 */
function updateActionsPosition(appSchema: Definition, schemaForActions: Definition, positionId: string) {
    const { properties } = schemaForActions;
    for (const property in properties) {
        const { $ref } = properties[property] as Definition;
        if ($ref) {
            const definitionKey = getDefinitionKey($ref);
            const definition = appSchema.definitions[definitionKey] as Definition;
            if (definition?.properties?.position) {
                (definition.properties.position as Definition).$ref = getDefinitionsRef(positionId);
            }
        }
    }
}

/**
 * Creates a custom actions menu definition in the given application schema using the provided parameters.
 *
 * @param {Definition} appSchema - The application schema object where the custom menu definition will be created.
 * @param {string} lineItemId - The identifier for the line item associated with the custom menu definition.
 * @param {string} positionId - The identifier for the position to be set in the custom menu definition.
 * @param {DefinitionName} customActionMenuDefinitionKey - The key for the generic action menu definition used as a base for customization.
 */
function createCustomMenuDefinition(
    appSchema: Definition,
    lineItemId: string,
    positionId: string,
    customActionMenuDefinitionKey: DefinitionName
) {
    // Create the specific action menu definition in the schema based on the generic action menu definition
    const customActionMenuDefinition = parseSchemaDefinition(customActionMenuDefinitionKey, lineItemId, appSchema);
    const customActionMenuActionsDefinitionKey = getDefinitionKey(
        (customActionMenuDefinition?.properties[PropertyName.actions] as Definition)?.$ref
    );
    parseSchemaDefinition(customActionMenuActionsDefinitionKey, lineItemId, appSchema);
    (customActionMenuDefinition.properties.position as Definition).$ref = getDefinitionsRef(positionId);
    (customActionMenuDefinition.properties.actions as Definition).$ref = getDefinitionsRef(
        customActionMenuActionsDefinitionKey
    );
}
